/*
 * Pure data types for address validation.
 *
 * These live in src/model rather than beside the service because they carry
 * no Angular dependency and are needed by AddressValidationOrder and by the
 * standalone smoke tests, neither of which can load @angular/core. The
 * service re-exports them, so every existing import path still resolves.
 */
import {TextFragment, Util} from "../app/util/Util";

export class ValidationAddress {
  title: string;
  firstname: string;
  lastname: string;
  company: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  state: string;
  country: string;
  postalcode: string;

  constructor (obj) {
    this.title = '';
    this.firstname = '';
    this.lastname = '';
    this.company = '';
    this.address1 = '';
    this.address2 = '';
    this.address3 = '';
    this.address4 = '';
    this.city = '';
    this.state = '';
    this.country = 'USA';
    this.postalcode = '';

    if (obj) {
      for (const prop of Object.keys(obj)) {
        this[prop] = obj[prop];
      }
      if ( obj.country && obj.country.shortCode ) {
        this.country = obj.country.shortCode;
      }
    }
    if ( !this.country || this.country === 'US') {
      this.country = 'USA';
    }
  }
}

/**
 * What a completed validation actually told us, reduced to the cases the UI has
 * to treat differently.
 *
 * The distinction this type exists to protect is the one between `ERROR` and
 * `NOT_FOUND`. They were indistinguishable before: `validate()` turned any
 * transport failure into `undefined`, and every caller's `if (result)` guard
 * then either retried forever or fell through to the same "no suggestions"
 * rendering as a validator that had answered perfectly well and simply did not
 * recognise the address. Those are opposite facts about an address -- one says
 * nothing is known about it, the other says something definite and bad -- and an
 * operator shown the wrong one either re-types a good address or ships to a bad
 * one.
 *
 *   NOT_REQUESTED         nothing has been asked yet
 *   PENDING               asked, still waiting. Held on the order, not here
 *   ERROR                 the request failed. The address is UNJUDGED
 *   UNSUPPORTED_COUNTRY   no validator covers this country. Also unjudged, but
 *                         permanently so -- the one case where proceeding
 *                         unvalidated is a legitimate answer rather than a way
 *                         round the gate
 *   NOT_FOUND             the validator answered and could not identify the
 *                         address. Definite, and a reason not to ship
 *   EXACT_MATCH           recognised exactly as supplied
 *   SUGGESTIONS           recognised, with corrections on offer
 */
export type AddressValidationOutcome =
  'NOT_REQUESTED' | 'PENDING' | 'ERROR' | 'UNSUPPORTED_COUNTRY' | 'NOT_FOUND' | 'EXACT_MATCH' | 'SUGGESTIONS';

export class AddressValidationResult {
  public recommendedAddresses: ValidationAddress[];
  public validationResult: string;
  /**
   * Why the request failed, when `validationResult` is `ERROR` because the call
   * itself did not complete. Empty for every outcome the validator reported.
   */
  public errorMessage: string;

  constructor (obj) {
    this.validationResult = '';
    this.recommendedAddresses = [];
    this.errorMessage = '';

    if (obj) {
      for (const prop of Object.keys(obj)) {
        this[prop] = obj[prop];
      }
      this.recommendedAddresses = [];
      if ( obj.recommendedAddresses ) {
        for ( const recommendedAddress of obj.recommendedAddresses ) {
          this.recommendedAddresses.push( new ValidationAddress( recommendedAddress ) );
        }
      }
    }
  }

  // public enum ValidationResult {
  // 	NO_COUNTRY_MATCH, //If we do not have an address validator for the requested country, then this is the validated result.
  // 	EXACT_MATCH, //There is not a single difference between the suggestion and the requested address.
  // 	NO_MEANINGFUL_SUGGESTIONS, //The difference between the suggestion and the requested address may have been because it was not in all caps (like UPS wants it to be for some reason)
  // 	HAVE_SUGGESTIONS, //There is one or more suggestions
  // 	ERROR //There was an error with the request
  // }

  // ---------------------------------------------------------------------------
  // `validationResult` is FREE TEXT. The enum above was never implemented.
  //
  // Confirmed by the server on 2026-09-23 (see
  // docs/contracts/inbox/_processed/2026-09-23-address-precedence-confirmed-manual-override-legal-and-validationresult-free-text.md):
  // `EXACT_MATCH`, `NO_MEANINGFUL_SUGGESTIONS` and `HAVE_SUGGESTIONS` are
  // emitted by NOTHING. The values that can actually arrive are:
  //
  //   'Success'                      the US validator's fallback when UPS
  //                                  returned no status description
  //   <raw UPS statusDescription>    whatever UPS said, verbatim
  //   'NO_COUNTRY_MATCH'             no validator for that country. Only the US
  //                                  validator is registered, so EVERY non-US
  //                                  address lands here
  //   'ERROR'                        the controller's initial value
  //   'Error validating address. A'  the controller's catch block, trailing "A"
  //                                  and all
  //   'Could not authenticate'       UPS auth failure
  //
  // The last three are FAILURES OF THE CALL, not verdicts about the address.
  // Left to the old `default:` branch they were decided on the suggestion list,
  // which is empty for all of them -- so a UPS outage or an expired credential
  // rendered as "could not identify this as a valid address". That is the exact
  // lie this class exists to prevent, and it is worse than useless here: it
  // tells an operator the address is bad when the truth is that nothing checked
  // it, which invites them to "correct" an address that was right all along.
  //
  // The enum names are still accepted below. They cost nothing, and the server
  // has offered to normalise this response later.
  // ---------------------------------------------------------------------------

  /**
   * Does this free-text value describe a broken CALL rather than a judged
   * address?
   *
   * Deliberately conservative: it matches the shapes the server documented plus
   * the obvious transport words, and nothing else. A false positive here is
   * mild (an address reads as unchecked instead of unrecognised, and the
   * operator is asked to validate again); a false negative is the bug above.
   */
  private static looksLikeCallFailure (value: string): boolean {
    const text = ( value ? value : '' ).trim().toLowerCase();
    if ( text.length === 0 ) {
      return true;
    }
    return text.indexOf('error') === 0
      || text.indexOf('could not authenticate') !== -1
      || text.indexOf('unable to') !== -1
      || text.indexOf('exception') !== -1
      || text.indexOf('timed out') !== -1
      || text.indexOf('timeout') !== -1;
  }

  /**
   * The single question every caller actually wants answered.
   *
   * Order matters. `NO_COUNTRY_MATCH` is tested before anything looks at
   * `recommendedAddresses`, because for that outcome the server echoes the
   * address you just sent it back into that array -- so a non-empty list there
   * means "here is your own address again", not "validated". Deciding on the
   * list first would read every non-US address as an exact match.
   */
  getOutcome (): AddressValidationOutcome {
    const hasSuggestions = !!( this.recommendedAddresses && this.recommendedAddresses.length > 0 );
    switch ( this.validationResult ) {
      case 'NO_COUNTRY_MATCH':
        return 'UNSUPPORTED_COUNTRY';
      // Never emitted today; accepted so that a future normalisation does not
      // need a client release to be understood.
      case 'EXACT_MATCH':
        return 'EXACT_MATCH';
      case 'NO_MEANINGFUL_SUGGESTIONS':
        return 'NOT_FOUND';
      case 'HAVE_SUGGESTIONS':
      case 'Success':
      case 'SUCCESS':
        return hasSuggestions ? 'SUGGESTIONS' : 'NOT_FOUND';
      default:
        // Free text. Anything that reads as a broken call is unjudged; anything
        // else is a status description from a validator that did run, so the
        // suggestion list is the only thing left that can decide.
        if ( AddressValidationResult.looksLikeCallFailure(this.validationResult) ) {
          return 'ERROR';
        }
        return hasSuggestions ? 'SUGGESTIONS' : 'NOT_FOUND';
    }
  }

  /** True when the validator reached a verdict about the address, good or bad. */
  isConclusive (): boolean {
    const outcome = this.getOutcome();
    return outcome === 'EXACT_MATCH' || outcome === 'SUGGESTIONS' || outcome === 'NOT_FOUND';
  }

  /**
   * A result standing for "the call did not complete", so callers receive an
   * object carrying that fact instead of an `undefined` carrying nothing.
   */
  static forError (message: string): AddressValidationResult {
    const result = new AddressValidationResult(undefined);
    result.validationResult = 'ERROR';
    result.errorMessage = message ? message : '';
    return result;
  }
}


/**
 * One recommended address, with its comparison against the submitted address
 * ALREADY COMPUTED.
 *
 * ## Why this is precomputed and not done in the template
 *
 * `address.component.html` used to call
 * `Util.getSameAndDifferentFragments(...)` directly from six `*ngFor`
 * expressions. A function call in `*ngFor` re-runs on every change-detection
 * pass -- every notification tick, every keystroke, every mouse move -- and
 * this one ALLOCATES. So each pass handed `NgForOf` six brand-new arrays of
 * brand-new objects, which its identity-based differ reads as "every item was
 * removed and new ones added", tearing down and rebuilding that DOM
 * continuously.
 *
 * That was merely wasteful until one of the inputs was undefined. The helper
 * did `textB.trim()` unguarded, and `AddressValidationOrder.address2` is
 * seeded from `bookorder.originalAddress?.address2`, which is `undefined`
 * whenever the imported address had no second line. The throw landed in the
 * middle of `NgForOf` applying its diff: the views it had already inserted
 * stayed, the rest of the pass was abandoned, and the next tick started over
 * and inserted them again. The visible symptom was a suggested address that
 * grew by one copy of the street line every time a notification arrived --
 * roughly every fifteen seconds -- until the panel was unreadable.
 *
 * Computing once, when the result arrives, addresses the cause rather than the
 * symptom: the arrays are stable, `NgForOf` sees no change, and there is no
 * per-pass work left to throw from.
 */
export class AddressSuggestion {
  address: ValidationAddress;
  address1Fragments: TextFragment[] = [];
  address2Fragments: TextFragment[] = [];
  cityFragments: TextFragment[] = [];
  stateFragments: TextFragment[] = [];
  postalCodeFragments: TextFragment[] = [];
  countryFragments: TextFragment[] = [];

  /**
   * True when this suggestion describes a RANGE of addresses ("100-110 MAIN
   * ST") rather than one deliverable address, so it cannot be shipped to as it
   * stands.
   *
   * Per suggestion, deliberately. The check this replaces
   * (`AddressValidationOrder.doesAddressHaveARangeCharacter()`) asked about
   * `verifiedAddress1` / `verifiedAddress2` -- the address already CHOSEN --
   * while being used to decide whether to show the button that does the
   * choosing. One ranged candidate therefore suppressed the accept button on
   * every other suggestion too, including good ones, leaving the operator with
   * a list they could not act on and nothing explaining why.
   */
  hasRangeCharacter: boolean = false;

  /**
   * `original` is structurally typed rather than declared as an
   * `AddressValidationOrder` on purpose: that class imports this file, and
   * naming it here would close the cycle.
   */
  static build (address: ValidationAddress, original: {
    address1?: string, address2?: string, city?: string,
    region?: string, postalCode?: string, country?: string }): AddressSuggestion {
    const suggestion = new AddressSuggestion();
    suggestion.address = address;
    suggestion.address1Fragments = Util.getSameAndDifferentFragments(address.address1, original.address1, ' ');
    suggestion.address2Fragments = Util.getSameAndDifferentFragments(address.address2, original.address2, ' ');
    suggestion.cityFragments = Util.getSameAndDifferentFragments(address.city, original.city, ' ');
    suggestion.stateFragments = Util.getSameAndDifferentFragments(address.state, original.region, ' ');
    suggestion.postalCodeFragments = Util.getSameAndDifferentFragments(address.postalcode, original.postalCode, '-');
    suggestion.countryFragments = Util.getSameAndDifferentFragments(address.country, original.country, ' ');
    const street = ( address.address1 ? address.address1 : '' ) + ( address.address2 ? address.address2 : '' );
    suggestion.hasRangeCharacter = street.indexOf('-') >= 0;
    return suggestion;
  }
}

